import React from "react";
import styles from "./styles.module.css";
import RouletteBg from "../images/Roulette/RouletteBg.png";
import Roulli from "../images/Roulette/Roulli.svg";
import RoulettePin from "../images/Roulette/RoulettePin.svg";
import crownImg from "../images/Roulette/crown.png";

const numberOrder = [
  0, 32, 15, 19, 4, 21, 2, 25, 17, 34, 6, 27, 13, 36, 11, 30, 8, 23, 10, 5,
  24, 16, 33, 1, 20, 14, 31, 9, 22, 18, 29, 7, 28, 12, 35, 3, 26,
];

const numberAngleMap = {};
const anglePerSlot = 360 / 37;
numberOrder.forEach((num, index) => {
  numberAngleMap[num] = index * anglePerSlot;
});

// smoother ease-out (quint) — no jerk at any point of the spin
const easeOut = (t) => 1 - Math.pow(1 - t, 5);

const GameScreen = ({ socketData, resultNumber }) => {
  const [rotation, setRotation] = React.useState(0);
  const [showWinner, setShowWinner] = React.useState(false);
  const [betsHidden, setBetsHidden] = React.useState(false);

  const rotationRef = React.useRef(0);
  const animationFrameRef = React.useRef(null);
  const spinKeyRef = React.useRef(null);
  const isSpinningRef = React.useRef(false);
  const hasSnappedRef = React.useRef(false);

  React.useEffect(() => {
    rotationRef.current = rotation;
  }, [rotation]);

  const startSpin = (winningNumber, spinDuration) => {
    if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);

    const extraSpins = 10 + Math.floor(Math.random() * 3);
    const currentModulo = ((rotationRef.current % 360) + 360) % 360;
    const targetOffset = (360 - (numberAngleMap[winningNumber] % 360)) % 360;
    const deltaToTarget = (targetOffset - currentModulo + 360) % 360;
    const targetAngle = rotationRef.current + extraSpins * 360 + deltaToTarget;
    const startAngle = rotationRef.current;
    const startTime = performance.now();

    isSpinningRef.current = true;

    const step = (now) => {
      const elapsed = now - startTime;
      const t = Math.min(elapsed / spinDuration, 1);
      const current = startAngle + (targetAngle - startAngle) * easeOut(t);
      setRotation(current);
      rotationRef.current = current;
      if (t < 1) {
        animationFrameRef.current = requestAnimationFrame(step);
      } else {
        animationFrameRef.current = null;
        isSpinningRef.current = false;
      }
    };
    animationFrameRef.current = requestAnimationFrame(step);
  };

  const snapToZero = () => {
    if (hasSnappedRef.current) return;
    hasSnappedRef.current = true;
    if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);

    const snapStart = rotationRef.current;
    const snapTarget = Math.ceil(snapStart / 360) * 360;
    const duration = 1500;
    const startTime = performance.now();

    isSpinningRef.current = true;

    const snapStep = (now) => {
      const t = Math.min((now - startTime) / duration, 1);
      const current = snapStart + (snapTarget - snapStart) * easeOut(t);
      setRotation(current);
      rotationRef.current = current;
      if (t < 1) {
        animationFrameRef.current = requestAnimationFrame(snapStep);
      } else {
        animationFrameRef.current = null;
        isSpinningRef.current = false;
      }
    };
    animationFrameRef.current = requestAnimationFrame(snapStep);
  };

  React.useEffect(() => {
    if (!socketData || socketData.type !== "TIMER_UPDATE") return;

    const { bettingClosed, animationTime, roundId, settlement } = socketData;

    // New round starting -> reset everything, but never interrupt an active spin
    if (!bettingClosed) {
      if (!isSpinningRef.current) {
        hasSnappedRef.current = false;
        spinKeyRef.current = null;
        setShowWinner(false);
        setBetsHidden(false);
      }
      return;
    }

    // Hide bets as soon as betting closes
    setBetsHidden(true);

    const resNum = settlement?.resultNumber;
    const winnerTime = settlement?.winnerTime;
    const resultTime = settlement?.resultTime;

    // Result not in yet -> keep waiting, don't show winner
    if (resNum == null) {
      setShowWinner(false);
      return;
    }

    const spinKey = `${roundId}_${resNum}`;

    // Result arrived for the first time this round -> start the spin (only once per round)
    if (spinKey !== spinKeyRef.current) {
      spinKeyRef.current = spinKey;
      hasSnappedRef.current = false;
      setShowWinner(false);
      const spinDuration = Math.max((animationTime - (winnerTime ?? 5)) * 1000, 1000);
      startSpin(resNum, spinDuration);
      return;
    }

    // Winner display window
    if (winnerTime != null || resultTime != null) {
      setShowWinner(true);
    }
  }, [socketData]);

  // Snap back to zero once a new round begins and the wheel is free (not mid-spin)
  React.useEffect(() => {
    if (!socketData) return;
    const { bettingClosed } = socketData;
    if (!bettingClosed && !isSpinningRef.current) {
      snapToZero();
    }
  }, [socketData?.bettingClosed]);

  React.useEffect(() => () => {
    if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
  }, []);

  return (
    <div className={styles.rouletteGameContainer}>
      <div className={styles.rouletteWheelArea}>
        <div className={styles.rouletteMainContent}>
          <div
            className={styles.rouletteBackground}
            style={{ backgroundImage: `url('${RouletteBg}')` }}
          />
          <div className={styles.rouletteWheelContainer}>
            <div className={styles.rouletteWheelWrapper}>
              <img
                src={RoulettePin}
                alt="Roulette Pin"
                className={styles.roulettePin}
                draggable={false}
              />
              <img
                src={Roulli}
                alt="Roulette Wheel"
                style={{ transform: `rotate(${rotation}deg)`, transition: "none" }}
                className={styles.rouletteWheel}
                draggable={false}
              />
            </div>
          </div>

          {showWinner && (
            <div className={styles.rouletteWinnerOverlay}>
              <img src={crownImg} alt="Winner" className={styles.rouletteWinnerCrown} />
              <span className={styles.rouletteWinnerNumber}>
                {socketData?.settlement?.resultNumber ?? ""}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GameScreen;